import { useState, useEffect, useRef } from "react";
import { Settings } from "lucide-react";
import levelsData from "../data/levels.json";
import gameConfig from "../data/gameConfig.json";
import apiFetch from "../apiFetch";
import { getSession } from "../session";
import "../styles/userSettingsModal.css";

const MAX_TIER_BELOW_16 = gameConfig.ageGroup.belowCutoffTier;
const MAX_TIER_ABOVE_16 = 21;

const SLIDERS = [
  { key: "synonyms_elo",  label: "Synonyms" },
  { key: "antonyms_elo",  label: "Antonyms" },
  { key: "vocab_elo",     label: "Vocab Matching" },
];

export default function UserSettingsModal() {
  const session  = getSession();
  const userId   = session?.userId;
  const ageGroup = localStorage.getItem("ageGroup") || "below_16";
  const maxTier  = ageGroup === "above_16" ? MAX_TIER_ABOVE_16 : MAX_TIER_BELOW_16;

  const availableTiers = Object.entries(levelsData.tiers).filter(
    ([key]) => parseInt(key, 10) <= maxTier
  );

  const [open, setOpen]     = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const [error, setError]   = useState("");
  const panelRef            = useRef(null);

  const [owlletId, setOwlletId]       = useState(null);
  const [tier, setTier]               = useState("");
  const [synonymsElo, setSynonymsElo] = useState(0);
  const [antonymsElo, setAntonymsElo] = useState(0);
  const [vocabElo, setVocabElo]       = useState(0);

  // Track original ELO values to detect slider changes
  const [origElo, setOrigElo] = useState({ synonyms_elo: 0, antonyms_elo: 0, vocab_elo: 0 });

  const eloChanged =
    synonymsElo !== origElo.synonyms_elo ||
    antonymsElo !== origElo.antonyms_elo ||
    vocabElo    !== origElo.vocab_elo;

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Load current settings whenever panel opens
  useEffect(() => {
    if (!open || !userId) return;
    setError("");
    apiFetch(`/api/user/settings/${userId}`)
      .then((r) => r.json())
      .then((d) => {
        const t  = d.difficulty_tier ? String(d.difficulty_tier) : "";
        const s  = d.synonyms_elo ?? 0;
        const a  = d.antonyms_elo ?? 0;
        const v  = d.vocab_elo    ?? 0;
        if (d.owllet_id) setOwlletId(d.owllet_id);
        setTier(t);
        setSynonymsElo(s);
        setAntonymsElo(a);
        setVocabElo(v);
        setOrigElo({ synonyms_elo: s, antonyms_elo: a, vocab_elo: v });
      })
      .catch(() => setError("Failed to load settings."));
  }, [open, userId]);

  const handleApply = async () => {
    if (!userId) return;
    setSaving(true);
    setError("");
    try {
      const res  = await apiFetch("/api/user/settings", {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({
          userId:       parseInt(userId, 10),
          difficulty_tier: tier ? parseInt(tier, 10) : null,
          synonyms_elo: synonymsElo,
          antonyms_elo: antonymsElo,
          vocab_elo:    vocabElo,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Save failed");

      if (tier) localStorage.setItem("difficulty_tier", tier);
      else      localStorage.removeItem("difficulty_tier");

      setOrigElo({ synonyms_elo: synonymsElo, antonyms_elo: antonymsElo, vocab_elo: vocabElo });
      setSaved(true);
      setTimeout(() => { setSaved(false); setOpen(false); }, 1200);
    } catch {
      setError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const eloState = {
    synonyms_elo:  [synonymsElo, setSynonymsElo],
    antonyms_elo:  [antonymsElo, setAntonymsElo],
    vocab_elo:     [vocabElo,    setVocabElo],
  };

  return (
    <div className="us-wrap" ref={panelRef}>
      <button
        className="us-trigger"
        onClick={() => setOpen((p) => !p)}
        title="Settings"
        aria-label="Open preferences"
      >
        <Settings size={22} />
      </button>

      {open && (
        <div className="us-panel">
          <div className="us-panel-header">
            <p className="us-panel-title">Preferences</p>
          </div>

          <div className="us-panel-body">

          {/* ── Owllet ID ── */}
          {owlletId && (
            <div className="us-owllet-id-block">
              <span className="us-owllet-id-label">Owllet ID</span>
              <span className="us-owllet-id-value">{owlletId}</span>
            </div>
          )}

          {/* ── Difficulty Tier ── */}
          <div className="us-section">
            <label className="us-label" htmlFor="us-tier-select">
              Default Difficulty Tier
            </label>
            <select
              id="us-tier-select"
              className="us-select"
              value={tier}
              onChange={(e) => setTier(e.target.value)}
            >
              <option value="">— Not set —</option>
              {availableTiers.map(([key, t]) => (
                <option key={key} value={key}>
                  {key}. {t.name}
                </option>
              ))}
            </select>
            <p className="us-hint">Pre-selects this tier in game setup screens.</p>
          </div>

          {/* ── ELO Sliders ── */}
          <div className="us-section">
            <p className="us-section-title">Vocabulary ELO</p>
            {SLIDERS.map(({ key, label }) => {
              const [val, setVal] = eloState[key];
              return (
                <div key={key} className="us-slider-group">
                  <div className="us-slider-header">
                    <span className="us-slider-label">{label}</span>
                    <span className="us-slider-value">{val}</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="250"
                    value={val}
                    onChange={(e) => setVal(Number(e.target.value))}
                    className="us-slider"
                  />
                  <div className="us-slider-ticks">
                    <span>0</span>
                    <span>250</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── ELO warning ── */}
          {eloChanged && (
            <div className="us-elo-warning">
              <strong>⚠️ Warning:</strong> Applying will overwrite your current ELO
              score for the changed games. Word complexity in vocabulary games is
              determined by your ELO — adjusting it will change the difficulty you
              encounter immediately.
            </div>
          )}

          {error && <p className="us-error">{error}</p>}

          {/* ── Actions ── */}
          <div className="us-actions">
            <button className="us-cancel" onClick={() => setOpen(false)}>
              Cancel
            </button>
            <button
              className="us-apply"
              onClick={handleApply}
              disabled={saving}
            >
              {saved ? "Saved!" : saving ? "Saving…" : "Apply"}
            </button>
          </div>

          </div>{/* end us-panel-body */}
        </div>
      )}
    </div>
  );
}
